import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../api/config';
import { useToastContext } from '../common/Toast';
import { handleApiError } from '../../utils/errorHandler';

interface JobCard {
  id: number;
  job_number: string;
  status: string;
  priority: string;
  customer?: {
    name: string;
    phone_number: string;
  };
  vehicle?: {
    make: string;
    model: string;
    plate_number: string;
  };
  assigned_employee?: {
    name: string;
  };
  service_type: string;
  description: string;
  estimated_cost: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface WorkflowTransition {
  from_status: string;
  to_status: string;
  label: string;
  icon: string;
  color: string;
  requires_notes: boolean;
}

interface WorkflowHistory {
  id: number;
  from_status: string;
  to_status: string;
  changed_by: {
    name: string;
  };
  notes: string;
  created_at: string;
}

interface JobCardWorkflowModalProps {
  jobCard: JobCard;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: () => void;
}

const JobCardWorkflowModal: React.FC<JobCardWorkflowModalProps> = ({
  jobCard,
  isOpen,
  onClose,
  onStatusChange
}) => {
  const { showSuccess, showError } = useToastContext();
  const [allowedTransitions, setAllowedTransitions] = useState<WorkflowTransition[]>([]);
  const [workflowHistory, setWorkflowHistory] = useState<WorkflowHistory[]>([]);
  const [selectedTransition, setSelectedTransition] = useState<WorkflowTransition | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && jobCard.id) {
      fetchWorkflowData();
    }
  }, [isOpen, jobCard.id]);

  const fetchWorkflowData = async () => {
    try {
      const [transitionsRes, historyRes] = await Promise.all([
        apiClient.get(`/job-cards/${jobCard.id}/workflow/transitions`),
        apiClient.get(`/job-cards/${jobCard.id}/workflow/history`)
      ]);

      setAllowedTransitions(transitionsRes.data.allowed_transitions || transitionsRes.data.transitions || []);
      setWorkflowHistory(historyRes.data.history || historyRes.data.data || []);
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleTransition = async () => {
    if (!selectedTransition) return;

    try {
      setLoading(true);
      await apiClient.post(`/job-cards/${jobCard.id}/workflow/transition`, {
        new_status: selectedTransition.to_status,
        notes: notes
      });

      showSuccess('Status updated successfully');
      onStatusChange();
      onClose();
      setSelectedTransition(null);
      setNotes('');
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'in_progress':
        return <WrenchScrewdriverIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Job Card Workflow</h2>
            <p className="text-sm text-gray-600">
              {jobCard.job_number} - {jobCard.service_type}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Current Status */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Current Status</h3>
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(jobCard.status)}
              <div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(jobCard.status)}`}>
                  {jobCard.status.replace('_', ' ').toUpperCase()}
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {jobCard.assigned_employee?.name ? `Assigned to ${jobCard.assigned_employee.name}` : 'Unassigned'}
                </p>
              </div>
            </div>
          </div>

          {/* Available Transitions */}
          {allowedTransitions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {allowedTransitions.map((transition, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedTransition(transition)}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      selectedTransition?.to_status === transition.to_status
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(transition.to_status)}
                        <span className="font-medium">{transition.label}</span>
                      </div>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Transition to {transition.to_status.replace('_', ' ')}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes for Selected Transition */}
          {selectedTransition && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">
                Transition to {selectedTransition.to_status.replace('_', ' ').toUpperCase()}
              </h4>
              {selectedTransition.requires_notes && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Required)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Add notes about this status change..."
                    required={selectedTransition.requires_notes}
                  />
                </div>
              )}
              <div className="flex space-x-2">
                <button
                  onClick={handleTransition}
                  disabled={loading || (selectedTransition.requires_notes && !notes.trim())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Confirm Transition'}
                </button>
                <button
                  onClick={() => {
                    setSelectedTransition(null);
                    setNotes('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Workflow History */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Workflow History</h3>
            <div className="space-y-3">
              {workflowHistory.length > 0 ? (
                workflowHistory.map((history, index) => (
                  <div key={history.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      {getStatusIcon(history.to_status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">
                          {history.from_status ? `${history.from_status} → ${history.to_status}` : `Set to ${history.to_status}`}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(history.to_status)}`}>
                          {history.to_status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          {history.changed_by.name}
                        </span>
                        <span className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDateTime(history.created_at)}
                        </span>
                      </div>
                      {history.notes && (
                        <p className="mt-2 text-sm text-gray-700 bg-white p-2 rounded border">
                          {history.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">
                  No workflow history available
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCardWorkflowModal;